"use client";

import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";
import { SubsidiaryModal } from "../components/SubsidiaryModal";
import { SubsidiaryTable } from "../components/SubsidiaryTable";
import { Subsidiary } from "../types";

export default function SettingsSubsidiaries() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [subsidiaries, setSubsidiaries] = useState<Subsidiary[]>([]);
  const [isSubsidiaryModalOpen, setIsSubsidiaryModalOpen] = useState(false);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState<
    Subsidiary | undefined
  >();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSubsidiaries = useCallback(
    async (companyId: string) => {
      try {
        const { data: subsidiariesData, error: subsidiariesError } =
          await supabase
            .from("subsidiaries")
            .select("*")
            .eq("company_id", companyId);

        if (subsidiariesError) throw subsidiariesError;
        setSubsidiaries(subsidiariesData || []);
      } catch (error) {
        console.error("Error fetching subsidiaries:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Gesellschaften",
          variant: "destructive"
        });
      }
    },
    [toast]
  );

  const fetchCompanyId = useCallback(async () => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("id", profile.id)
        .single();

      if (profileError) throw profileError;
      setCompanyId(profileData?.company_id);
      if (profileData?.company_id) {
        await fetchSubsidiaries(profileData.company_id);
      }
    } catch (error) {
      console.error("Error fetching company ID:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Laden der Unternehmensdaten",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [profile.id, toast, fetchSubsidiaries]);

  useEffect(() => {
    if (!profile.anonymous) {
      fetchCompanyId();
    }
  }, [profile, fetchCompanyId]);

  const handleSubsidiarySave = async (
    subsidiaryData: Omit<
      Subsidiary,
      "id" | "company_id" | "created_at" | "updated_at"
    >
  ) => {
    if (!companyId) {
      toast({
        title: "Fehler",
        description: "Keine Unternehmensdaten gefunden",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      if (selectedSubsidiary) {
        // Update existing subsidiary
        const { error: subsidiaryError } = await supabase
          .from("subsidiaries")
          .update({
            name: subsidiaryData.name,
            location: subsidiaryData.location,
            updated_at: new Date().toISOString()
          })
          .eq("id", selectedSubsidiary.id);

        if (subsidiaryError) throw subsidiaryError;

        toast({
          title: "Erfolg",
          description: "Gesellschaft erfolgreich aktualisiert"
        });
      } else {
        // Create new subsidiary
        const { error: subsidiaryError } = await supabase
          .from("subsidiaries")
          .insert({
            name: subsidiaryData.name,
            location: subsidiaryData.location,
            company_id: companyId
          });

        if (subsidiaryError) throw subsidiaryError;

        toast({
          title: "Erfolg",
          description: "Gesellschaft erfolgreich erstellt"
        });
      }

      await fetchSubsidiaries(companyId);
      setIsSubsidiaryModalOpen(false);
      setSelectedSubsidiary(undefined);
    } catch (error) {
      console.error("Error saving subsidiary:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Speichern der Gesellschaft",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubsidiaryDelete = async (subsidiary: Subsidiary) => {
    try {
      const { error: deleteError } = await supabase
        .from("subsidiaries")
        .delete()
        .eq("id", subsidiary.id);

      if (deleteError) throw deleteError;

      toast({
        title: "Erfolg",
        description: "Gesellschaft erfolgreich gelöscht"
      });
      if (companyId) {
        await fetchSubsidiaries(companyId);
      }
    } catch (error) {
      console.error("Error deleting subsidiary:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Löschen der Gesellschaft",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-500">Lade Daten...</div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-500">Keine Unternehmensdaten gefunden</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Gesellschaften</h2>
        <p className="text-sm text-gray-500">
          Verwalte die Gesellschaften deines Unternehmens
        </p>
      </div>
      <div>
        <SubsidiaryTable
          subsidiaries={subsidiaries}
          onEdit={(subsidiary) => {
            setSelectedSubsidiary(subsidiary);
            setIsSubsidiaryModalOpen(true);
          }}
          onDelete={handleSubsidiaryDelete}
          onAdd={() => {
            setSelectedSubsidiary(undefined);
            setIsSubsidiaryModalOpen(true);
          }}
        />
      </div>

      <SubsidiaryModal
        open={isSubsidiaryModalOpen}
        onOpenChange={setIsSubsidiaryModalOpen}
        onSave={handleSubsidiarySave}
        subsidiary={selectedSubsidiary}
        title={
          selectedSubsidiary ? "Gesellschaft bearbeiten" : "Neue Gesellschaft"
        }
        isSaving={isSaving}
      />
    </div>
  );
}
